import React from 'react';
import moment from 'moment';
import {Alert, Button} from 'fearless-common-ui';
import useApiCall from '../hooks/useApiCall';
import {defaultDateTimeFormat} from '../../utils/date';

interface Props {
    /**
     * Base URL of the resource being archived/restored, e.g. `/admin/courses/5`.
     * - Archive action sends `DELETE {baseUrl}`
     * - Restore action sends `PUT {baseUrl}/restore`
     */
    baseUrl: string;
    /** The resource's `deleted_at` value. When set, the restore UI is shown; otherwise the archive UI. */
    deletedAt: string | null | undefined;
    /** Human-readable noun used in button labels and descriptions, e.g. `"course"`. */
    resourceName: string;
    /** Called after a successful archive or restore so the parent can refresh its data. */
    onSuccess?: () => void;
    /** Optional content appended inside the archive description paragraph. */
    children?: React.ReactNode;
}

/**
 * Reusable toggle button that handles both archiving (soft-delete) and restoring
 * a resource in the admin area.
 *
 * - When `deletedAt` is falsy it renders the **Archive** UI: a description paragraph
 *   (with optional `children` appended) and a danger-outlined button that sends `DELETE {baseUrl}`.
 * - When `deletedAt` is set it renders the **Restore** UI: a danger alert showing
 *   the archive timestamp and a success button that sends `PUT {baseUrl}/restore`.
 *
 * After either action completes successfully, `onSuccess` is called so the parent
 * can reload its data.
 */
export default function ArchiveToggleButton({ baseUrl, deletedAt, resourceName, onSuccess, children }: Props) {
    const { onSubmit: doDelete, isSubmitting: isDeleting, error: deleteError } = useApiCall({
        url: baseUrl,
        method: 'delete',
    });

    const { onSubmit: doRestore, isSubmitting: isRestoring, error: restoreError } = useApiCall({
        url: `${baseUrl}/restore`,
        method: 'put',
    });

    if (deletedAt) {
        return (
            <>
                <h6>Restore {resourceName}</h6>
                <Alert variant="danger" className="p-1">
                    This {resourceName} was archived at {moment(deletedAt).format(defaultDateTimeFormat)}.
                    {restoreError && <><br />{restoreError}</>}
                </Alert>
                <Button
                    size="sm"
                    variant="success"
                    onClick={() => doRestore().then(() => onSuccess?.())}
                    isDoing={isRestoring}
                >
                    Restore {resourceName}
                </Button>
            </>
        );
    }

    return (
        <>
            <h6>Archive {resourceName}</h6>
            <p className="small line-height-1-2">
                Archive this {resourceName}. This does NOT permanently delete it or its data.
                {children}
            </p>
            {deleteError && <Alert>{deleteError}</Alert>}
            <Button
                variant="outline-danger"
                size="sm"
                className="mb-2"
                isDoing={isDeleting}
                onClick={() => doDelete().then(() => onSuccess?.())}
            >
                Archive this {resourceName}
            </Button>
        </>
    );
}
